import React, { useRef, useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, AlertCircle, ChevronLeft, ShieldCheck, CheckCircle } from 'lucide-react';
import HUDFrame from './ui/HUDFrame';
import ArmyLogo from './united-states-army-2023-seeklogo.png';

interface PhotoCaptureProps {
  onCapture: (imageData: string) => void;
  onBack?: () => void;
}

const PhotoCapture: React.FC<PhotoCaptureProps> = ({ onCapture, onBack }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasConsented, setHasConsented] = useState(false);

  // Initialize webcam only after consent
  useEffect(() => {
    if (!hasConsented) return;

    const initCamera = async () => {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            facingMode: 'user'
          },
        });
        setStream(mediaStream);
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      } catch (err) {
        console.error('Error accessing camera:', err);
        setError('Unable to access camera. Please check permissions.');
      }
    };

    initCamera();

    // Cleanup handled by separate effect watching stream
  }, [hasConsented]);

  // Cleanup stream on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  // Update video source when stream changes
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    onBack?.();
  }, [stream, onBack]);

  // Handle photo capture
  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    let count = 3;
    setCountdown(count);

    const countdownInterval = setInterval(() => {
      count -= 1;

      if (count <= 0) {
        clearInterval(countdownInterval);
        setCountdown(null);

        // Take the photo after a small delay to ensure state is updated
        setTimeout(() => {
          const video = videoRef.current;
          const canvas = canvasRef.current;

          if (!video || !canvas) return;

          const context = canvas.getContext('2d');
          if (!context) return;

          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          context.drawImage(video, 0, 0);

          const imageData = canvas.toDataURL('image/png');

          // Stop the stream
          if (stream) {
            stream.getTracks().forEach(track => track.stop());
          }

          // Send the captured image
          onCapture(imageData);
        }, 100);
      } else {
        setCountdown(count);
      }
    }, 1000);
  }, [stream, onCapture]);

  if (error) {
    return (
      <motion.div
        className="h-full w-full flex items-center justify-center bg-army-black"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="text-center space-y-4">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto" />
          <h2 className="text-2xl text-white">{error}</h2>
          <p className="text-gray-400">Please ensure camera permissions are enabled.</p>
        </div>
      </motion.div>
    );
  }

  // Consent/Disclaimer Screen
  if (!hasConsented) {
    return (
      <motion.div
        className="h-full w-full flex items-center justify-center bg-army-black"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        <HUDFrame className="w-full max-w-4xl 4k:max-w-[120rem] mx-auto p-8 4k:p-32">
          <div className="text-center space-y-8 4k:space-y-16">
            {/* Back Button */}
            {onBack && (
              <motion.button
                onClick={onBack}
                className="absolute left-6 top-20 4k:top-40 flex items-center space-x-2 4k:space-x-4 text-army-gold hover:text-white transition-colors group z-10"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                whileHover={{ x: -5 }}
              >
                <ChevronLeft className="w-6 h-6 4k:w-16 4k:h-16" />
                <span className="text-sm 4k:text-3xl uppercase tracking-wider font-bold">Back</span>
              </motion.button>
            )}

            {/* Army Logo */}
            <motion.div
              initial={{ y: -30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.6 }}
            >
              <img src={ArmyLogo} alt="U.S. Army" className="h-20 4k:h-64 w-auto mx-auto" />
            </motion.div>

            {/* Shield Icon */}
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5, type: 'spring' }}
            >
              <ShieldCheck className="w-24 h-24 4k:w-64 4k:h-64 text-army-gold mx-auto" />
            </motion.div>

            {/* Header */}
            <motion.div
              initial={{ y: -30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              <h2 className="text-2xl 4k:text-6xl text-army-gold uppercase tracking-widest mb-3 4k:mb-8">
                Photo Consent
              </h2>
              <h1 className="text-4xl 4k:text-[8rem] font-bold text-white leading-tight">
                BEFORE WE CONTINUE
              </h1>
            </motion.div>

            {/* Disclaimer Text */}
            <motion.div
              className="max-w-2xl 4k:max-w-[80rem] mx-auto space-y-4 4k:space-y-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              <p className="text-lg 4k:text-4xl text-gray-300 leading-relaxed">
                By proceeding, you consent to having your photo captured and processed
                using artificial intelligence to create your personalized Army portrait.
              </p>
              <div className="bg-army-dark/60 border border-army-gold/40 rounded-lg p-6 4k:p-12 text-left space-y-3 4k:space-y-6">
                <div className="flex items-start space-x-3 4k:space-x-6">
                  <CheckCircle className="w-5 h-5 4k:w-10 4k:h-10 text-hud-green mt-0.5 flex-shrink-0" />
                  <p className="text-base 4k:text-3xl text-gray-300">
                    Your photo will be used solely for creating your AI-generated Army portrait
                  </p>
                </div>
                <div className="flex items-start space-x-3 4k:space-x-6">
                  <CheckCircle className="w-5 h-5 4k:w-10 4k:h-10 text-hud-green mt-0.5 flex-shrink-0" />
                  <p className="text-base 4k:text-3xl text-gray-300">
                    Images are processed securely and stored temporarily
                  </p>
                </div>
                <div className="flex items-start space-x-3 4k:space-x-6">
                  <CheckCircle className="w-5 h-5 4k:w-10 4k:h-10 text-hud-green mt-0.5 flex-shrink-0" />
                  <p className="text-base 4k:text-3xl text-gray-300">
                    You will receive a QR code to download your personalized portraits
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Consent Button */}
            <motion.button
              onClick={() => setHasConsented(true)}
              className="relative group"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="relative bg-army-olive border-4 4k:border-12 border-army-gold px-12 4k:px-36 py-6 4k:py-16 flex items-center space-x-4 4k:space-x-10">
                <ShieldCheck className="w-8 h-8 4k:w-24 4k:h-24 text-army-gold" />
                <span className="text-2xl 4k:text-7xl font-bold text-white uppercase tracking-wider">
                  I Consent - Continue
                </span>
              </div>

              {/* Pulse effect */}
              <motion.div
                className="absolute inset-0 border-4 4k:border-8 border-army-gold"
                animate={{
                  scale: [1, 1.1, 1.1],
                  opacity: [0.5, 0, 0],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                }}
              />
            </motion.button>

            {/* Bottom Status */}
            <motion.div
              className="pt-4 4k:pt-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.6 }}
              transition={{ delay: 0.9 }}
            >
              <p className="text-base 4k:text-3xl text-gray-500 uppercase tracking-widest">
                Step 4 of 5 • Photo Consent
              </p>
            </motion.div>
          </div>
        </HUDFrame>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="h-full w-full flex items-center justify-center bg-army-black"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <HUDFrame className="w-full max-w-6xl 4k:max-w-[180rem] mx-auto p-8 4k:p-32">
        <div className="text-center space-y-8 4k:space-y-24">
          {/* Back Button */}
          {onBack && (
            <motion.button
              onClick={handleBack}
              className="absolute left-6 top-20 4k:top-40 flex items-center space-x-2 4k:space-x-4 text-army-gold hover:text-white transition-colors group z-10"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              whileHover={{ x: -5 }}
            >
              <ChevronLeft className="w-6 h-6 4k:w-16 4k:h-16" />
              <span className="text-sm 4k:text-3xl uppercase tracking-wider font-bold">Back</span>
            </motion.button>
          )}

          {/* Army Logo */}
          <motion.div
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.6 }}
          >
            <img src={ArmyLogo} alt="U.S. Army" className="h-20 4k:h-64 w-auto mx-auto" />
          </motion.div>

          {/* Header */}
          <motion.div
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <h2 className="text-3xl 4k:text-8xl text-army-gold uppercase tracking-widest mb-3 4k:mb-12">
              Biometric Capture
            </h2>
            <h1 className="text-5xl 4k:text-[12rem] font-bold text-white leading-none">
              FACE SCAN ACTIVE
            </h1>
          </motion.div>

          {/* Progress indicator */}
          <motion.div
            className="flex items-center justify-center space-x-3 4k:space-x-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <div className="w-16 4k:w-32 h-1.5 4k:h-3 bg-army-gold" />
            <div className="w-16 4k:w-32 h-1.5 4k:h-3 bg-army-gold" />
            <div className="w-16 4k:w-32 h-1.5 4k:h-3 bg-army-gold" />
            <div className="w-16 4k:w-32 h-1.5 4k:h-3 bg-army-gold" />
            <div className="w-16 4k:w-32 h-1.5 4k:h-3 bg-gray-600" />
          </motion.div>

          {/* Video Feed Container */}
          <div className="relative aspect-video max-w-4xl 4k:max-w-[100rem] mx-auto bg-black border-4 4k:border-8 border-army-gold overflow-hidden">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />

            {/* HUD Overlay */}
            <div className="absolute inset-0 pointer-events-none">
              {/* Scanning lines */}
              <motion.div
                className="absolute left-0 right-0 h-1 4k:h-2 bg-gradient-to-r from-transparent via-hud-green to-transparent"
                animate={{
                  y: [0, 480, 0],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'linear',
                }}
              />

              {/* Face targeting brackets */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-80 4k:w-[48rem] h-96 4k:h-[56rem]">
                  {/* Top left */}
                  <div className="absolute top-0 left-0 w-20 4k:w-40 h-20 4k:h-40">
                    <div className="absolute top-0 left-0 w-full h-1 4k:h-2 bg-hud-green" />
                    <div className="absolute top-0 left-0 w-1 4k:w-2 h-full bg-hud-green" />
                  </div>
                  {/* Top right */}
                  <div className="absolute top-0 right-0 w-20 4k:w-40 h-20 4k:h-40">
                    <div className="absolute top-0 right-0 w-full h-1 4k:h-2 bg-hud-green" />
                    <div className="absolute top-0 right-0 w-1 4k:w-2 h-full bg-hud-green" />
                  </div>
                  {/* Bottom left */}
                  <div className="absolute bottom-0 left-0 w-20 4k:w-40 h-20 4k:h-40">
                    <div className="absolute bottom-0 left-0 w-full h-1 4k:h-2 bg-hud-green" />
                    <div className="absolute bottom-0 left-0 w-1 4k:w-2 h-full bg-hud-green" />
                  </div>
                  {/* Bottom right */}
                  <div className="absolute bottom-0 right-0 w-20 4k:w-40 h-20 4k:h-40">
                    <div className="absolute bottom-0 right-0 w-full h-1 4k:h-2 bg-hud-green" />
                    <div className="absolute bottom-0 right-0 w-1 4k:w-2 h-full bg-hud-green" />
                  </div>

                  {/* Center crosshair */}
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <div className="relative">
                      <div className="absolute -left-10 4k:-left-20 w-20 4k:w-40 h-1 4k:h-2 bg-hud-green opacity-50" />
                      <div className="absolute -top-10 4k:-top-20 w-1 4k:w-2 h-20 4k:h-40 bg-hud-green opacity-50" />
                    </div>
                  </div>
                </div>
              </div>

              {/* HUD Text */}
              <div className="absolute top-6 left-6 4k:top-12 4k:left-12 text-hud-green font-hud text-base 4k:text-3xl space-y-1 4k:space-y-2">
                <p>SCAN MODE: ACTIVE</p>
                <p>RESOLUTION: 1280x720</p>
                <p>FPS: 30</p>
              </div>

              <div className="absolute top-6 right-6 4k:top-12 4k:right-12 text-hud-green font-hud text-base 4k:text-3xl text-right space-y-1 4k:space-y-2">
                <p>FACE DETECTED</p>
                <p>TRACKING: LOCKED</p>
                <motion.p
                  animate={{ opacity: [1, 0.5, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  ● REC
                </motion.p>
              </div>
            </div>

            {/* Countdown Overlay */}
            <AnimatePresence>
              {countdown !== null && (
                <motion.div
                  className="absolute inset-0 flex items-center justify-center bg-black/50"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <motion.div
                    key={countdown}
                    className="text-9xl font-bold text-army-gold glow-text"
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 1.5, opacity: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    {countdown}
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Hidden canvas for capture */}
          <canvas ref={canvasRef} className="hidden" />

          {/* Capture Button */}
          <motion.button
            onClick={capturePhoto}
            disabled={countdown !== null}
            className="relative group"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div className="relative bg-army-olive border-4 4k:border-12 border-army-gold px-16 4k:px-48 py-8 4k:py-24 flex items-center space-x-6 4k:space-x-16">
              <Camera className="w-10 h-10 4k:w-40 4k:h-40 text-army-gold" />
              <span className="text-3xl 4k:text-9xl font-bold text-white uppercase tracking-wider">
                Capture Image
              </span>
            </div>

            {/* Pulse effect */}
            <motion.div
              className="absolute inset-0 border-4 4k:border-8 border-army-gold"
              animate={{
                scale: [1, 1.1, 1.1],
                opacity: [0.5, 0, 0],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
              }}
            />
          </motion.button>

          {/* Instructions */}
          <motion.p
            className="text-xl 4k:text-6xl text-gray-400"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            Position your face within the targeting brackets
          </motion.p>

          {/* Bottom Status */}
          <motion.div
            className="pt-4 4k:pt-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.6 }}
            transition={{ delay: 1 }}
          >
            <p className="text-base 4k:text-3xl text-gray-500 uppercase tracking-widest">
              Step 4 of 5 • Biometric Capture
            </p>
          </motion.div>
        </div>
      </HUDFrame>
    </motion.div>
  );
};

export default PhotoCapture;